import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const base = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  expectedVersion: z.number().int().nonnegative(),
  receivableId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
}).strict()

const invoice = base.extend({
  action: z.literal("invoice"),
  expectedVersion: z.literal(0),
  customer: z.string().trim().min(1).max(140),
  itemCode: z.string().trim().min(1).max(140),
  costCenter: z.string().trim().min(1).max(140),
  incomeAccount: z.string().trim().min(1).max(140),
  receivableAccount: z.string().trim().min(1).max(140),
  postingDate: z.iso.date(),
  dueDate: z.iso.date(),
  amountMinor: z.number().int().positive(),
  description: z.string().trim().min(1).max(1_000),
})

const payment = base.extend({
  action: z.literal("payment"),
  paymentId: entityIdSchema,
  postingDate: z.iso.date(),
  amountMinor: z.number().int().positive(),
  paidToAccount: z.string().trim().min(1).max(140),
  referenceNumber: z.string().trim().min(1).max(140),
  referenceDate: z.iso.date(),
})

const credit = base.extend({
  action: z.literal("credit"),
  creditId: entityIdSchema,
  postingDate: z.iso.date(),
  amountMinor: z.number().int().positive(),
  reason: z.string().trim().min(1).max(1_000),
})

const writeOff = base.extend({
  action: z.literal("write_off"),
  writeOffId: entityIdSchema,
  postingDate: z.iso.date(),
  amountMinor: z.number().int().positive(),
  expenseAccount: z.string().trim().min(1).max(140),
  reason: z.string().trim().min(1).max(1_000),
})

const correction = base.extend({
  action: z.literal("correct"),
  correctionId: entityIdSchema,
  postingDate: z.iso.date(),
  amountMinor: z.number().int().positive(),
  reason: z.string().trim().min(1).max(1_000),
})

export const accountsReceivableActionV1Schema = z.discriminatedUnion(
  "action",
  [invoice, payment, credit, writeOff, correction],
)

export const accountsReceivableResultV1Schema = z.object({
  entityType: z.literal("receivable"),
  entityId: entityIdSchema,
  state: z.enum(["open", "partially_paid", "settled", "credited"]),
  version: z.number().int().positive(),
  legalEntityId: legalEntityIdSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  invoicedMinor: z.number().int().positive(),
  outstandingMinor: z.number().int().nonnegative(),
  salesInvoice: z.string().trim().min(1),
  paymentEntry: z.string().trim().min(1).nullable(),
  creditNote: z.string().trim().min(1).nullable(),
  writeOffJournalEntry: z.string().trim().min(1).nullable(),
  correctionDocument: z.string().trim().min(1).nullable(),
}).strict()

export const accountsReceivableAgingQueryV1Schema = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema.optional(),
  asOfDate: z.iso.date(),
  cursor: z.string().max(256).nullable(),
  limit: z.number().int().min(1).max(100),
}).strict()

export type AccountsReceivableActionV1 = z.infer<
  typeof accountsReceivableActionV1Schema
>
